import { Add as PlusIcon, Settings as SettingsIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AuthGuard from '@/components/auth-guard';
import { EventsTimeline } from '@/components/events-timeline';
import { Page } from '@/components/page';
import { SubscriptionLimitGate } from '@/components/subscription/subscription-limit-gate';
import { useEventAccessLayer } from '@/features/events';
import { useToast } from '@/hooks/use-toast';
import { useNovelRepository } from '@/hooks/useNovelRepository';
import { EntitySummary, EventSummary } from '@common/schemas/entities/summary.schema';

export const Route = createFileRoute('/events/')({
  component: EventsPage,
});

function EventsContent() {
  const {
    novel,
    batchSaveItemsNonBlocking,
    updateAllEventSummaries,
  } = useNovelRepository();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [localEvents, setLocalEvents] = useState<EventSummary[] | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { t } = useTranslation();

  const { events: accessEvents, isLoading, deleteEvent } = useEventAccessLayer();

  const events = accessEvents as EventSummary[];
  const characters = useMemo(() => Object.values(novel.summaries.characters), [novel]);
  const organizations = useMemo(() => Object.values(novel.summaries.organizations), [novel]);
  const locations = useMemo(() => Object.values(novel.summaries.locations), [novel]);

  const entityNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    characters.forEach(c => map[c.id] = c.name);
    organizations.forEach(o => map[o.id] = o.name);
    locations.forEach(l => map[l.id] = l.name);
    return map;
  }, [characters, organizations, locations]);

  const nextOrder = useMemo(() => {
    if (!events || events.length === 0) return 0;
    return Math.max(...events.map(e => e.order ?? 0)) + 1;
  }, [events]);

  useEffect(() => {
    if (events) {
      const sorted = [...events].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setLocalEvents(sorted);
    }
  }, [events]);

  const displayedEvents = useMemo(() => {
    if (!localEvents) return null;
    if (!searchQuery.trim()) return localEvents;
    const lowerQuery = searchQuery.toLowerCase();
    return localEvents.filter(e => (e.name || '').toLowerCase().includes(lowerQuery));
  }, [localEvents, searchQuery]);

  const handleReorder = useCallback(async (newOrder: EventSummary[]) => {
    setLocalEvents(newOrder);
    if (!novel.id) return;

    try {
      const updatedEvents = newOrder.map((e, idx) => ({
        id: e.id,
        order: idx,
      }));

      batchSaveItemsNonBlocking('entities', 'events', updatedEvents);

      const summaries: Record<string, EventSummary> = {};
      newOrder.forEach((e, idx) => {
        summaries[e.id] = {
          ...e,
          order: idx,
        };
      });
      await updateAllEventSummaries(summaries);
    } catch (error) {
      console.error('Failed to reorder events', error);
      toast({
        title: t('entity_list.events.reorder_failed'),
        description: t('entity_list.events.reorder_failed_desc'),
        variant: 'destructive',
      });
    }
  }, [novel, toast, t, batchSaveItemsNonBlocking, updateAllEventSummaries]);

  const handleInsert = useCallback((order: number) => {
    navigate({ to: '/events/new', search: { order: String(order) } });
  }, [navigate]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await deleteEvent(id);
      toast({
        title: t('common.success'),
        description: t('entity_form.events.delete_success_desc'),
      });
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast({
        title: t('common.error'),
        description: t('entity_form.events.delete_failed_desc'),
        variant: 'destructive',
      });
    }
  }, [deleteEvent, toast, t]);

  return (
    <AuthGuard>
      <Page
        title={t('entity_list.events.title')}
        description={t('entity_list.events.description')}
        layoutMode="scrolling"
        actions={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={t('entity_list.events.settings')}>
              <IconButton
                onClick={() => navigate({ to: '/events/settings' })}
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                  borderRadius: 2,
                }}
              >
                <SettingsIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
            <SubscriptionLimitGate limit="eventsPerNovel" currentCount={events.length}>
            <Button
              variant="contained"
              component={Link}
              to={`/events/new?order=${nextOrder}`}
              disabled={!novel.id}
              startIcon={<PlusIcon sx={{ fontSize: 18 }} />}
              sx={{ borderRadius: 2 }}
            >
              {t('entity_list.events.new')}
            </Button>
            </SubscriptionLimitGate>
          </Box>
        }
      >

        {isLoading && !novel.id && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!novel.id && !isLoading && (
          <Card sx={{ maxWidth: 500, mx: 'auto', mt: 4, textAlign: 'center', p: 4, borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h2" gutterBottom>{t('entity_list.no_novel_title')}</Typography>
              <Typography variant="body1" color="text.secondary">
                {t('entity_list.no_novel_desc', { entity: t('sidebar.events').toLowerCase() })}
              </Typography>
            </CardContent>
          </Card>
        )}

        {novel.id && localEvents && displayedEvents && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3, gap: 2 }}>
              <Box sx={{ position: 'relative', width: { xs: '100%', sm: 240 } }}>
                <Box sx={{
                  bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 1.5, py: 0.5,
                  display: 'flex', alignItems: 'center', gap: 1
                }}>
                  <InputBase
                    placeholder="Search events..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ fontSize: '0.85rem', width: '100%' }}
                  />
                </Box>
              </Box>
              <Box sx={{
                bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider', borderRadius: 2, px: 1.5, py: 0.75,
                display: 'flex', alignItems: 'center', gap: 1
              }}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}><span style={{ opacity: 0.5 }}>Sort by:</span> Chronological</Typography>
              </Box>
            </Box>
            <EventsTimeline
              events={displayedEvents}
              onReorder={handleReorder}
              entityNameMap={entityNameMap}
              novelId={novel.id}
              onInsert={handleInsert}
              onDelete={handleDelete}
              isDragDisabled={!!searchQuery.trim()}
            />
          </Box>
        )}
      </Page>
    </AuthGuard>
  );
}

function EventsPage() {
  return (
    <Suspense fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    }>
      <EventsContent />
    </Suspense>
  );
}
